import React from "react";
import { useNavigate } from "react-router-dom";
import { useApiData } from "@/hooks/useApiData";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Truck, CheckCircle2, CalendarClock, ReceiptText, MapPin, User, Phone, Mail, ArrowRight, AlertCircle, Building2, CalendarPlus, FileText } from "lucide-react";
import { formatDate, wasteLabel } from "@/lib/format";

const KPI = ({ icon: Icon, label, value, accent, testId }) => (
  <Card data-testid={testId} className={`rounded-xl border bg-white p-5 shadow-[var(--shadow-sm)] ${accent ? "border-t-4 border-t-[var(--hh-amber)]" : ""}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wide text-[color:var(--hh-slate)]">{label}</span>
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--hh-amber-50)] text-[color:var(--hh-amber-600)]">
        <Icon className="h-[18px] w-[18px]" />
      </span>
    </div>
    <div className="mt-3 font-display text-3xl font-semibold tracking-tight text-[color:var(--hh-ink)]">{value}</div>
  </Card>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApiData("/portal/overview");

  if (loading) {
    return (
      <div>
        <Skeleton className="mb-6 h-36 w-full rounded-2xl" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-64 rounded-xl lg:col-span-2" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" data-testid="dashboard-error">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Couldn't load your dashboard</AlertTitle>
        <AlertDescription className="flex items-center gap-3">
          {error}
          <Button size="sm" variant="outline" onClick={refetch}>Retry</Button>
        </AlertDescription>
      </Alert>
    );
  }

  const customer = data?.customer || {};
  const stats = data?.stats || {};
  const next = data?.next_collection;
  const firstName = (customer.contact_name || customer.name || "").split(" ")[0];
  const addressLine = [customer.city, customer.postcode].filter(Boolean).join(", ");

  return (
    <div data-testid="dashboard-page">
      {/* Branded hero header */}
      <div className="relative mb-6 overflow-hidden rounded-2xl bg-[color:var(--hh-ink)] p-6 text-white shadow-[var(--shadow-md)] sm:p-8" data-testid="dashboard-hero">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(600px circle at 12% 20%, rgba(245,166,35,0.55), transparent 45%), radial-gradient(520px circle at 92% 95%, rgba(229,143,14,0.4), transparent 45%)" }}
        />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--hh-amber)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--hh-amber)]" /> {customer.reference ? `Account ${customer.reference}` : "Customer Portal"}
            </span>
            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-[2rem] sm:leading-tight">
              Welcome back{firstName ? `, ${firstName}` : ""}
            </h1>
            <p className="mt-1.5 truncate text-sm text-white/70">
              {customer.name}{addressLine ? ` · ${addressLine}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Button onClick={() => navigate("/requests")} className="rounded-xl bg-[hsl(var(--primary))] font-semibold text-white hover:bg-[var(--hh-amber-600)]" data-testid="dashboard-request-button">
              <CalendarPlus className="mr-1.5 h-4 w-4" /> Request a collection
            </Button>
            <Button onClick={() => navigate("/documents")} variant="outline" className="rounded-xl border-white/25 bg-white/5 text-white hover:bg-white/10 hover:text-white" data-testid="dashboard-documents-button">
              <FileText className="mr-1.5 h-4 w-4" /> Documents
            </Button>
          </div>
        </div>
        {next && (
          <div className="relative mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-white/10 pt-4 text-sm">
            <div className="flex items-center gap-2 text-white/80">
              <CalendarClock className="h-4 w-4 text-[color:var(--hh-amber)]" />
              Next collection:&nbsp;<span className="font-semibold text-white">{formatDate(next.scheduled_date, { weekday: "short", day: "2-digit", month: "short" })}</span>{next.scheduled_time ? ` at ${next.scheduled_time}` : ""}
            </div>
            {next.status && <StatusBadge status={next.status} testId="hero-next-status" />}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KPI icon={Truck} label="Total collections" value={stats.total_collections ?? 0} accent testId="kpi-total" />
        <KPI icon={CheckCircle2} label="Completed" value={stats.completed_collections ?? 0} testId="kpi-completed" />
        <KPI icon={CalendarClock} label="Upcoming" value={stats.upcoming_collections ?? 0} testId="kpi-upcoming" />
        <KPI icon={ReceiptText} label="Open invoices" value={stats.open_invoices ?? 0} testId="kpi-open-invoices" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {/* Next collection */}
        <Card className="rounded-xl border bg-white p-6 shadow-[var(--shadow-sm)] lg:col-span-2" data-testid="next-collection-card">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-[color:var(--hh-ink)]">Next collection</h3>
            {next?.status && <StatusBadge status={next.status} testId="next-collection-status" />}
          </div>
          {next ? (
            <div className="mt-4">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <span className="font-display text-3xl font-semibold text-[color:var(--hh-ink)]">{formatDate(next.scheduled_date, { weekday: "long", day: "2-digit", month: "long" })}</span>
                {next.scheduled_time && <span className="text-lg font-medium text-[color:var(--hh-slate)]">at {next.scheduled_time}</span>}
              </div>
              <div className="mt-3 flex items-start gap-2 text-sm text-[color:var(--hh-slate)]">
                <MapPin className="mt-0.5 h-4 w-4 text-[color:var(--hh-amber-600)]" />
                <span>{[next.address, next.postcode].filter(Boolean).join(", ")}</span>
              </div>
              {Array.isArray(next.waste_types) && next.waste_types.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {next.waste_types.map((w) => (
                    <span key={w} className="rounded-full border border-[color:var(--hh-line)] bg-[var(--hh-band)] px-3 py-1 text-xs font-medium text-[color:var(--hh-slate)]">{wasteLabel(w)}</span>
                  ))}
                </div>
              )}
              <Button variant="outline" className="mt-5 rounded-xl" onClick={() => navigate("/collections")} data-testid="view-collections-button">
                View all collections <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">No upcoming collection is currently scheduled.</p>
          )}
        </Card>

        {/* Account details */}
        <Card className="rounded-xl border bg-white p-6 shadow-[var(--shadow-sm)]" data-testid="account-card">
          <h3 className="font-display text-lg font-semibold text-[color:var(--hh-ink)]">Your account</h3>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-start gap-2.5">
              <Building2 className="mt-0.5 h-4 w-4 text-[color:var(--hh-amber-600)]" />
              <div><div className="font-medium text-[color:var(--hh-ink)]">{customer.name}</div><div className="text-muted-foreground">{[customer.address, customer.city, customer.postcode].filter(Boolean).join(", ")}</div></div>
            </div>
            {customer.contact_name && <div className="flex items-center gap-2.5"><User className="h-4 w-4 text-[color:var(--hh-amber-600)]" /><span className="text-[color:var(--hh-slate)]">{customer.contact_name}</span></div>}
            {customer.phone && <div className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-[color:var(--hh-amber-600)]" /><a href={`tel:${customer.phone}`} className="text-[color:var(--hh-slate)] hover:text-[color:var(--hh-amber-600)]">{customer.phone}</a></div>}
            {customer.email && <div className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-[color:var(--hh-amber-600)]" /><span className="break-all text-[color:var(--hh-slate)]">{customer.email}</span></div>}
          </div>
          {Array.isArray(customer.waste_streams) && customer.waste_streams.length > 0 && (
            <div className="mt-5 border-t border-[color:var(--hh-line)] pt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-[color:var(--hh-slate)]">Your services</div>
              <div className="mt-3 space-y-2">
                {customer.waste_streams.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-[color:var(--hh-ink)]">{wasteLabel(s.type)}</span>
                    <span className="text-muted-foreground">{s.bin_count} × bin · {s.frequency}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
